export const SUPPORTED_LOCALES = [
  'ar',
  'ar-SA',
  'bg-BG',
  'cs-CZ',
  'da-DK',
  'de-DE',
  'el-GR',
  'en',
  'en-US',
  'es-MX',
  'fi-FI',
  'fr-FR',
  'hr-HR',
  'hu-HU',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'lt-LT',
  'ms-MY',
  'nb-NO',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'ro-RO',
  'ru-RU',
  'sl-SI',
  'sv-SE',
  'th-TH',
  'uk-UA',
  'ur',
  'vi-VN',
  'zh',
  'zh-CN',
] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

const RTL_LOCALE_SET = new Set<AppLocale>(['ur', 'ar', 'ar-SA']);

export function isAppLocale(value: string): value is AppLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** BCP 47 tag for `Intl` (dates, etc.). App locale ids may differ from tags (e.g. `zh`). */
export function appLocaleToIntlTag(locale: string): string {
  if (!isAppLocale(locale)) return 'en';
  switch (locale) {
    case 'zh':
      return 'zh-CN';
    case 'ur':
      return 'ur-PK';
    case 'ar':
      return 'ar';
    default:
      return locale;
  }
}

/** Whether the locale uses RTL layout (`dir`) and RTL-oriented typography. */
export function isRtlLocale(locale: string): boolean {
  return isAppLocale(locale) && RTL_LOCALE_SET.has(locale);
}

export function normalizeLocationLanguage(raw: unknown): AppLocale {
  if (typeof raw === 'string' && isAppLocale(raw)) return raw;
  return 'en';
}

/** Radix `Sheet` slide-in edge: from inline-end in LTR (`right`), from inline-start in RTL (`left`). */
export function sheetContentSide(locale: string): 'left' | 'right' {
  return isRtlLocale(locale) ? 'left' : 'right';
}
